/**
 * Matrice des permissions exécutable — reflet de docs/MATRICE_PERMISSIONS.md §5 (refus par défaut).
 * Utilisée par les handlers MSW (simulation) et destinée aux guards NestJS (production).
 * En simulation, ces contrôles sont SIMULÉS : ils prouvent la logique, pas une protection réelle.
 */

export type RoleClient = 'CLIENT_ADMIN' | 'CLIENT_ACHETEUR' | 'CLIENT_COMPTABLE';
export type RoleInterne =
  | 'SUPER_ADMIN'
  | 'ADMIN_COMMERCIAL'
  | 'ACHETEUR_APPRO'
  | 'RESP_ENTREPOT'
  | 'PREPARATEUR'
  | 'COMPTABLE'
  | 'SUPPORT'
  | 'AUDITEUR';
export type Role = RoleClient | RoleInterne;

export const ROLES_CLIENT: RoleClient[] = ['CLIENT_ADMIN', 'CLIENT_ACHETEUR', 'CLIENT_COMPTABLE'];
export const ROLES_INTERNES: RoleInterne[] = [
  'SUPER_ADMIN',
  'ADMIN_COMMERCIAL',
  'ACHETEUR_APPRO',
  'RESP_ENTREPOT',
  'PREPARATEUR',
  'COMPTABLE',
  'SUPPORT',
  'AUDITEUR',
];

export const LIBELLE_ROLE: Record<Role, string> = {
  CLIENT_ADMIN: 'Administrateur entreprise',
  CLIENT_ACHETEUR: 'Acheteur',
  CLIENT_COMPTABLE: 'Comptabilité (client)',
  SUPER_ADMIN: 'Super-administrateur',
  ADMIN_COMMERCIAL: 'Administration commerciale',
  ACHETEUR_APPRO: 'Acheteur approvisionnement',
  RESP_ENTREPOT: "Responsable d'entrepôt",
  PREPARATEUR: 'Préparateur',
  COMPTABLE: 'Comptabilité',
  SUPPORT: 'Support client',
  AUDITEUR: 'Auditeur',
};

/** Règle d'attribution : portée limitée, MFA récente ★, quatre yeux ◆. */
export interface Regle {
  portee?: 'entreprise' | 'entrepot';
  mfaRecente?: boolean;
  quatreYeux?: boolean;
  /** Restriction textuelle supplémentaire, vérifiée par le handler (ex. « ses commandes »). */
  note?: string;
}

const E: Regle = { portee: 'entreprise' };
const W: Regle = { portee: 'entrepot' };
const ok: Regle = {};
const R: Regle = { mfaRecente: true };
const RQ: Regle = { mfaRecente: true, quatreYeux: true };

export const MATRICE = {
  'catalogue.public.lire': { CLIENT_ADMIN: ok, CLIENT_ACHETEUR: ok, CLIENT_COMPTABLE: ok },
  'portail.acces': { CLIENT_ADMIN: ok, CLIENT_ACHETEUR: ok, CLIENT_COMPTABLE: ok },
  'prix.voir': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: E, CLIENT_COMPTABLE: E },
  'panier.gerer': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: E },
  'commande.passer': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: E },
  'commande.lire.propres': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: E, CLIENT_COMPTABLE: E },
  'commande.annuler.propres': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: { ...E, note: 'ses commandes' } },
  'commande.choisir.propres': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: { ...E, note: 'ses commandes' } },
  'facture.lire.propres': { CLIENT_ADMIN: E, CLIENT_COMPTABLE: E },
  'entreprise.utilisateurs.gerer': { CLIENT_ADMIN: { ...E, mfaRecente: true } },
  'entreprise.adresses.gerer': { CLIENT_ADMIN: E },
  'compte.securite.gerer': { CLIENT_ADMIN: R, CLIENT_ACHETEUR: R, CLIENT_COMPTABLE: R },
  'rgpd.demande.propre': { CLIENT_ADMIN: ok, CLIENT_ACHETEUR: ok, CLIENT_COMPTABLE: ok },
  'accord.lire.propres': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: E, CLIENT_COMPTABLE: E },
  'devis.demander': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: E },
  'devis.lire.propres': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: E, CLIENT_COMPTABLE: E },
  'favoris.gerer': { CLIENT_ADMIN: E, CLIENT_ACHETEUR: E },

  'admin.acces': {
    SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: ok,
    PREPARATEUR: ok, COMPTABLE: ok, SUPPORT: ok, AUDITEUR: ok,
  },
  'client.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, COMPTABLE: ok, SUPPORT: ok },
  'client.inscription.valider': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok },
  'client.modifier': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok },
  'client.suspendre': { SUPER_ADMIN: R, ADMIN_COMMERCIAL: R },
  'produit.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: ok, PREPARATEUR: ok, SUPPORT: ok },
  'produit.gerer': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok },
  'tarif.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, COMPTABLE: ok },
  'tarif.gerer': { SUPER_ADMIN: R, ADMIN_COMMERCIAL: R },
  'fournisseur.lire': { SUPER_ADMIN: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: ok, COMPTABLE: ok },
  'fournisseur.gerer': { SUPER_ADMIN: ok, ACHETEUR_APPRO: ok },
  'fournisseur.banque.modifier': { SUPER_ADMIN: RQ, COMPTABLE: RQ },
  'achat.lire': { SUPER_ADMIN: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: W, PREPARATEUR: W, COMPTABLE: ok },
  'achat.gerer': { SUPER_ADMIN: ok, ACHETEUR_APPRO: ok },
  'reception.saisir': { SUPER_ADMIN: ok, RESP_ENTREPOT: W, PREPARATEUR: W },
  'import.executer': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: W },
  'entrepot.lire': {
    SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: W, PREPARATEUR: W, COMPTABLE: ok, SUPPORT: ok,
  },
  'entrepot.gerer': { SUPER_ADMIN: ok, RESP_ENTREPOT: { ...W, note: 'créneaux uniquement' } },
  'stock.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: W, PREPARATEUR: W, SUPPORT: ok },
  'stock.ajuster': { SUPER_ADMIN: ok, RESP_ENTREPOT: { ...W, quatreYeux: true, note: '◆ au-delà du seuil' } },
  'stock.transferer': { SUPER_ADMIN: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: { ...W, note: 'entrepôt source' } },
  'tracabilite.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: W },
  'commande.lire.toutes': {
    SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, RESP_ENTREPOT: W, PREPARATEUR: W, COMPTABLE: ok, SUPPORT: ok,
  },
  'commande.gerer': { SUPER_ADMIN: R, ADMIN_COMMERCIAL: R },
  'preparation.executer': { SUPER_ADMIN: ok, RESP_ENTREPOT: W, PREPARATEUR: W },
  'retrait.remettre': { SUPER_ADMIN: ok, RESP_ENTREPOT: W, PREPARATEUR: W },
  'expedition.gerer': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, RESP_ENTREPOT: W },
  'paiement.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, COMPTABLE: ok },
  'paiement.confirmer': {
    SUPER_ADMIN: ok,
    COMPTABLE: ok,
    RESP_ENTREPOT: { ...W, note: 'CB au retrait uniquement' },
    PREPARATEUR: { ...W, note: 'CB au retrait uniquement' },
  },
  'paiement.rembourser': { SUPER_ADMIN: RQ, COMPTABLE: RQ },
  'parametres.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, RESP_ENTREPOT: ok, COMPTABLE: ok, AUDITEUR: ok },
  'parametres.modifier': { SUPER_ADMIN: R, ADMIN_COMMERCIAL: { ...R, note: 'paramètres commerciaux' } },
  'utilisateur.interne.gerer': { SUPER_ADMIN: RQ },
  'securite.mfa.reinitialiser': { SUPER_ADMIN: RQ },
  'securite.sessions.revoquer': { SUPER_ADMIN: R },
  'securite.cles.gerer': { SUPER_ADMIN: RQ },
  'accord.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, COMPTABLE: ok },
  'accord.gerer': { SUPER_ADMIN: R, ADMIN_COMMERCIAL: R },
  'accord.valider': { SUPER_ADMIN: RQ, ADMIN_COMMERCIAL: RQ },
  'marge.exception.approuver': { SUPER_ADMIN: RQ, COMPTABLE: RQ },
  'devis.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, COMPTABLE: ok },
  'devis.gerer': { SUPER_ADMIN: R, ADMIN_COMMERCIAL: R },
  'livraison.gerer': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, RESP_ENTREPOT: W },
  'marge.lire': { SUPER_ADMIN: ok, ADMIN_COMMERCIAL: ok, ACHETEUR_APPRO: ok, COMPTABLE: ok },
  'couts.parametrer': { SUPER_ADMIN: R, COMPTABLE: R },
  'retour.enregistrer': { SUPER_ADMIN: R, ADMIN_COMMERCIAL: R },
  'audit.lire': { SUPER_ADMIN: ok, AUDITEUR: ok },
  'audit.exporter': { SUPER_ADMIN: R, AUDITEUR: R },
  'rgpd.traiter': { SUPER_ADMIN: R },
  // V2 — sauvegardes (SIMULÉES dans la maquette) : lecture/vérification ; création et restauration ★ réservées au super-admin.
  'sauvegarde.lire': { SUPER_ADMIN: ok, AUDITEUR: ok },
  'sauvegarde.gerer': { SUPER_ADMIN: R },
} as const satisfies Record<string, Partial<Record<Role, Regle>>>;

export type Permission = keyof typeof MATRICE | 'simulation.piloter';
export const PERMISSIONS = Object.keys(MATRICE) as (keyof typeof MATRICE)[];

/** Contexte d'autorisation tiré de la session (jamais de la requête). */
export interface SujetAutorisation {
  type: 'CLIENT' | 'INTERNE';
  roles: Role[];
  entrepriseId?: string | null;
  statutEntreprise?: string | null;
  porteeEntrepots?: string[] | null; // null = globale
  mfaVerifiee: boolean;
  mfaVerifieeLe?: string | null;
}

export interface RessourceCible {
  entrepriseId?: string | null;
  entrepotId?: string | null;
}

export type MotifRefus =
  | 'NON_AUTHENTIFIE'
  | 'MFA_REQUISE'
  | 'MFA_RECENTE_REQUISE'
  | 'PERMISSION_REFUSEE'
  | 'ENTREPRISE_INACTIVE'
  | 'HORS_PORTEE';

export type Decision = { ok: true; regle: Regle } | { ok: false; motif: MotifRefus };

export const DUREE_MFA_RECENTE_MS = 10 * 60 * 1000;

/** Permissions client lisibles par une entreprise SUSPENDUE (historique seulement — RM-07). */
const LECTURE_SUSPENDUE = new Set<string>(['portail.acces', 'commande.lire.propres', 'facture.lire.propres', 'compte.securite.gerer', 'rgpd.demande.propre', 'catalogue.public.lire']);

/** Règle la plus permissive parmi les rôles détenus (une règle sans portée l'emporte sur une règle à portée). */
export function regleApplicable(roles: Role[], permission: Permission): Regle | null {
  if (permission === 'simulation.piloter') return null;
  const lignes = MATRICE[permission] as Partial<Record<Role, Regle>>;
  const regles = roles.map((r) => lignes[r]).filter((r): r is Regle => !!r);
  if (!regles.length) return null;
  return regles.find((r) => !r.portee) ?? regles[0]!;
}

/**
 * Décision d'accès (C-MFA, C-MFA-RECENTE, C-ENT-ACTIVE, C-PROPRE-ENT, C-ENTREPOT).
 * Hors portée → HORS_PORTEE, que l'API traduit en 404 (jamais 403).
 */
export function autorise(
  sujet: SujetAutorisation | null,
  permission: Permission,
  cible: RessourceCible = {},
  maintenant: Date = new Date(),
): Decision {
  if (!sujet) return { ok: false, motif: 'NON_AUTHENTIFIE' };
  if (sujet.type === 'INTERNE' && !sujet.mfaVerifiee) return { ok: false, motif: 'MFA_REQUISE' };
  const regle = regleApplicable(sujet.roles, permission);
  if (!regle) return { ok: false, motif: 'PERMISSION_REFUSEE' };

  if (sujet.type === 'CLIENT') {
    const statut = sujet.statutEntreprise;
    if (statut !== 'ACTIVE' && !(statut === 'SUSPENDUE' && LECTURE_SUSPENDUE.has(permission))) {
      return { ok: false, motif: 'ENTREPRISE_INACTIVE' };
    }
    if (cible.entrepriseId && cible.entrepriseId !== sujet.entrepriseId) return { ok: false, motif: 'HORS_PORTEE' };
  }
  if (regle.portee === 'entrepot' && sujet.porteeEntrepots) {
    if (cible.entrepotId && !sujet.porteeEntrepots.includes(cible.entrepotId)) return { ok: false, motif: 'HORS_PORTEE' };
  }
  if (regle.mfaRecente) {
    const le = sujet.mfaVerifieeLe ? new Date(sujet.mfaVerifieeLe).getTime() : 0;
    if (maintenant.getTime() - le > DUREE_MFA_RECENTE_MS) return { ok: false, motif: 'MFA_RECENTE_REQUISE' };
  }
  return { ok: true, regle };
}

/** Toutes les permissions d'un ensemble de rôles (indicatif pour l'UI). */
export function permissionsDe(roles: Role[]): Permission[] {
  return PERMISSIONS.filter((p) => regleApplicable(roles, p));
}

/** Contrôle ◆ : l'approbateur doit être distinct du demandeur. */
export function peutApprouver(demandeurId: string, approbateurId: string): boolean {
  return demandeurId !== approbateurId;
}
